import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { MdOutlineClear } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { IGroup } from '~/types/group'

import AddRoleToGroupModal from './AddRoleToGroupModal'
import AddUserToGroupModal from './AddUserToGroupModal'

import Section, { SectionItem } from '../../../shared/Section'

import LayoutGroups from '~/components/Layouts/Groups'
import ModalDialog from '~/components/ModalDialog'
import UserCard from '~/components/UserCard'
import {
  keys as groupsKeys,
  useDeleteRoleFromGroupMutation,
  useDeleteUserFromGroupMutation,
} from '~/queries/user-groups'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  group: IGroup
}

const GroupsDetailsGroup = ({ group }: Props): ReactElement => {
  /** Local state */

  const { distinct_authorizations: distinctAuthorizations = [], id, roles = [], users = [] } = group

  const queryClient = useQueryClient()

  const [isDeleteRoleModalOpen, setDeleteRoleModalOpen] = useState(false)
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false)

  const [isAddGroupToRoleModalOpen, setIsAddRoleToGroupModalOpen] = useState(false)
  const [isAddUserToRoleModalOpen, setIsAddUserToGroupModalOpen] = useState(false)

  const [deletedRoleId, setDeletedRoleId] = useState<string>()
  const [deletedUserId, setDeletedUserId] = useState<string>()

  /** Queries & Mutations  */

  const { mutateAsync: deleteRolesMutation } = useDeleteRoleFromGroupMutation()
  const { mutateAsync: deleteUsersMutation } = useDeleteUserFromGroupMutation()

  /** Handlers */

  const handleDeleteRoleModalState = (isOpen: boolean, roleId?: string) => () => {
    setDeletedRoleId(roleId)
    setDeleteRoleModalOpen(isOpen)
  }

  const handleDeleteUserModalState = (isOpen: boolean, userId?: string) => () => {
    setDeletedUserId(userId)
    setIsDeleteUserModalOpen(isOpen)
  }

  const handleAddRoleToGroupFailure = (error: unknown) => {
    captureException(error)
    displayToast('Role(s) could not be added', 'error')
    setIsAddRoleToGroupModalOpen(false)
  }

  const handleAddUserToGroupFailure = (error: unknown) => {
    captureException(error)
    displayToast('User(s) could not be added', 'error')
    setIsAddUserToGroupModalOpen(false)
  }

  const handleAddRoleToGroupSuccess = () => {
    setIsAddRoleToGroupModalOpen(false)
    displayToast('Role(s) successfully added', 'success')
  }

  const handleAddUserToGroupSuccess = () => {
    setIsAddUserToGroupModalOpen(false)
    displayToast('User(s) successfully added', 'success')
  }

  const handleOpenAddRoleToGroupModal = (value: boolean) => {
    return () => setIsAddRoleToGroupModalOpen(value)
  }

  const handleOpenAddUserToGroupModal = (value: boolean) => {
    return () => setIsAddUserToGroupModalOpen(value)
  }

  const handleClickDeleteRole = async () => {
    if (deletedRoleId) {
      await handleDeleteRole([deletedRoleId])
    }

    setDeletedRoleId(undefined)
    setDeleteRoleModalOpen(false)
  }

  const handleClickDeleteUser = async () => {
    if (deletedUserId) {
      await handleDeleteUser(deletedUserId)
    }

    setDeletedUserId(undefined)
    setIsDeleteUserModalOpen(false)
  }

  const handleDeleteRole = async (roleIds: string[]) => {
    try {
      await deleteRolesMutation({ groupId: id, roleIds: roleIds })
      queryClient.invalidateQueries({ queryKey: groupsKeys.details(id) })
      displayToast('Role successfully removed', 'success')
    } catch {
      displayToast('Role could not be removed', 'error')
    }
  }

  const handleDeleteUser = async (userId: string) => {
    try {
      await deleteUsersMutation({ groupId: id, userId: userId })
      queryClient.invalidateQueries({ queryKey: groupsKeys.details(id) })
      displayToast('User successfully removed', 'success')
    } catch {
      displayToast('User could not be removed', 'error')
    }
  }

  /** Render */

  return (
    <>
      <LayoutGroups>
        <Section itemsCount={roles.length} onClickAdd={handleOpenAddRoleToGroupModal(true)} title="Related roles">
          {roles.map(({ description, name, role_id }) => (
            <SectionItem key={role_id} onDelete={handleDeleteRoleModalState(true, role_id)}>
              <div className="item-body">
                <div className="item-title">{name}</div>
                <div className="item-subtitle">{description}</div>
              </div>
            </SectionItem>
          ))}
        </Section>
        <Section
          className="group-section"
          itemsCount={users.length}
          onClickAdd={handleOpenAddUserToGroupModal(true)}
          title="Member users"
        >
          {users.map(({ email, name, picture, profile, user_id }) => (
            <SectionItem key={user_id} onDelete={handleDeleteUserModalState(true, user_id)}>
              <div className="user-row">
                <UserCard name={name} picture={picture} size="small" subInfo={profile?.job_title} />
                <div className="user-email">{email}</div>
              </div>
            </SectionItem>
          ))}
        </Section>
        <Section className="group-section" itemsCount={distinctAuthorizations.length} title="Authorizations">
          {distinctAuthorizations.map((authorizationName, key) =>
            authorizationName.length ? (
              <SectionItem className="group-section__auth-section-item" key={key}>
                <div className="auth-row">{authorizationName}</div>
              </SectionItem>
            ) : null
          )}
        </Section>
      </LayoutGroups>
      <AddRoleToGroupModal
        groupId={id}
        isOpen={isAddGroupToRoleModalOpen}
        onClose={handleOpenAddRoleToGroupModal(false)}
        onFailure={handleAddRoleToGroupFailure}
        onSuccess={handleAddRoleToGroupSuccess}
      />
      <AddUserToGroupModal
        groupId={id}
        isOpen={isAddUserToRoleModalOpen}
        onClose={handleOpenAddUserToGroupModal(false)}
        onFailure={handleAddUserToGroupFailure}
        onSuccess={handleAddUserToGroupSuccess}
      />
      <ModalDialog
        icon={<MdOutlineClear size={42} />}
        isOpen={isDeleteRoleModalOpen}
        mainText="Remove role"
        onClose={handleDeleteRoleModalState(false)}
        onConfirm={handleClickDeleteRole}
        subText="Are you sure you want to remove the role from the group?"
      />
      <ModalDialog
        icon={<MdOutlineClear size={42} />}
        isOpen={isDeleteUserModalOpen}
        mainText="Remove user"
        onClose={handleDeleteUserModalState(false)}
        onConfirm={handleClickDeleteUser}
        subText="Are you sure you want to remove the user from the group?"
      />
    </>
  )
}

export default GroupsDetailsGroup
